/**
 * Minimal typed fetch wrapper for the MyAiFriend backend. All cabinet pages go
 * through this so JWT handling, error normalisation and 401-redirect live
 * in one place.
 */

const TOKEN_KEY = "lucky.jwt";
const EMAIL_KEY = "lucky.email";

// Impersonation: when a superadmin "logs in as" a user, we stash the original
// (superadmin) token under these keys so we can restore it with one click.
const ORIG_TOKEN_KEY = "lucky.orig.jwt";
const ORIG_EMAIL_KEY = "lucky.orig.email";
const IMPERSONATE_FLAG = "lucky.impersonating";

export function startImpersonation(newToken: string, newEmail: string) {
  const curToken = getToken();
  const curEmail = getEmail();
  if (curToken) localStorage.setItem(ORIG_TOKEN_KEY, curToken);
  if (curEmail) localStorage.setItem(ORIG_EMAIL_KEY, curEmail);
  localStorage.setItem(IMPERSONATE_FLAG, "1");
  saveSession(newToken, newEmail);
}

export function stopImpersonation(): boolean {
  const token = localStorage.getItem(ORIG_TOKEN_KEY);
  const email = localStorage.getItem(ORIG_EMAIL_KEY);
  if (!token) return false;
  saveSession(token, email ?? "");
  localStorage.removeItem(ORIG_TOKEN_KEY);
  localStorage.removeItem(ORIG_EMAIL_KEY);
  localStorage.removeItem(IMPERSONATE_FLAG);
  return true;
}

export function isImpersonating(): boolean {
  return localStorage.getItem(IMPERSONATE_FLAG) === "1";
}

export type ApiResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; error: string };

const validationTranslations: Record<string, string> = {
  "must be a valid email": "укажите корректный e-mail",
  "must be at least 8 characters": "пароль должен содержать не менее 8 символов",
  "must be a valid invite code": "нужен код приглашения вида invite_…",
  "must be 'eu' or 'us'": "указан неподдерживаемый регион",
  "invite code is invalid or unavailable":
    "код приглашения недействителен, уже использован или истёк",
  "email already registered": "аккаунт с таким e-mail уже зарегистрирован",
};

function translateApiMessage(value: unknown): string {
  if (typeof value === "string") return validationTranslations[value] ?? value;
  if (typeof value === "object" && value) {
    const record = value as Record<string, unknown>;
    for (const key of ["message", "detail", "error"]) {
      const message = translateApiMessage(record[key]);
      if (message) return message;
    }
  }
  return "";
}

export function extractApiError(body: unknown, status: number): string {
  if (typeof body === "object" && body && "errors" in body) {
    const errors = (body as { errors?: unknown }).errors;
    if (typeof errors === "object" && errors) {
      const messages = Object.values(errors)
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .filter((value): value is string => typeof value === "string")
        .map(translateApiMessage);
      if (messages.length) return messages.join(". ");
    }
  }

  return (
    (typeof body === "object" && body && "error" in body && translateApiMessage((body as { error: unknown }).error)) ||
    (typeof body === "object" && body && "message" in body && translateApiMessage((body as { message: unknown }).message)) ||
    (typeof body === "object" && body && "detail" in body && translateApiMessage((body as { detail: unknown }).detail)) ||
    (typeof body === "string" && body) ||
    (status === 402 ? "Не хватает лимита подписки или постоянных кредитов. Проверьте остатки и источник оплаты в разделе «Расходы»." : `HTTP ${status}`)
  );
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function getEmail(): string | null {
  return localStorage.getItem(EMAIL_KEY);
}

interface TokenClaims {
  exp?: number;
  iat?: number;
  act?: string;
}

/** Read (not verify) JWT claims; the server remains the only authority. */
export function readTokenClaims(token: string): TokenClaims | null {
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const bytes = Uint8Array.from(atob(padded), (ch) => ch.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as TokenClaims;
  } catch {
    return null;
  }
}

/** A stored token that has not expired yet (with a small clock margin). */
export function hasLiveSession(now = Date.now()): boolean {
  const token = getToken();
  if (!token) return false;
  const exp = readTokenClaims(token)?.exp;
  return typeof exp === "number" && exp * 1000 > now + 30_000;
}

// Sliding session: renew roughly once a day while the cabinet is in use, so
// an active user is never asked to sign in again.
const RENEW_AFTER_MS = 24 * 60 * 60 * 1000;
let renewal: Promise<void> | null = null;

export function sessionRenewalDue(token: string, now = Date.now()): boolean {
  const claims = readTokenClaims(token);
  if (!claims || claims.act) return false; // impersonation stays short-lived
  if (typeof claims.exp !== "number" || claims.exp * 1000 <= now) return false;
  return typeof claims.iat !== "number" || now - claims.iat * 1000 >= RENEW_AFTER_MS;
}

export function renewSessionIfDue(): Promise<void> {
  const token = getToken();
  if (!token || !sessionRenewalDue(token) || renewal) return renewal ?? Promise.resolve();
  renewal = (async () => {
    try {
      const resp = await fetch("/v1/auth/refresh", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) return; // a 401/403 surfaces on the next regular request
      const body = (await resp.json()) as { accessToken?: string };
      // Another tab may have signed out or switched accounts meanwhile.
      if (body.accessToken && getToken() === token) {
        saveSession(body.accessToken, getEmail() ?? "");
      }
    } catch {
      // Offline: keep the current token and retry on the next check.
    } finally {
      renewal = null;
    }
  })();
  return renewal;
}

function clearChatClientState() {
  sessionStorage.removeItem("lucky.session_id");
  sessionStorage.removeItem("lucky.chat.messages");
  sessionStorage.removeItem("lucky.chat.history_after");
}

export function saveSession(token: string, email: string) {
  if (localStorage.getItem(TOKEN_KEY) !== token) clearChatClientState();
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(EMAIL_KEY, email || "");
}
export function clearSession() {
  clearChatClientState();
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EMAIL_KEY);
  localStorage.removeItem(ORIG_TOKEN_KEY);
  localStorage.removeItem(ORIG_EMAIL_KEY);
  localStorage.removeItem(IMPERSONATE_FLAG);
}

/**
 * Low-level request. 401 clears the session and reloads to /cabinet/login —
 * TanStack Query handlers above this don't need to repeat that.
 */
export async function apiRequest<T>(
  path: string,
  init: RequestInit = {}
): Promise<ApiResult<T>> {
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  let resp: Response;
  try {
    resp = await fetch(path, { ...init, headers });
  } catch (e) {
    return { ok: false, status: 0, error: e instanceof Error ? e.message : "network" };
  }

  if (resp.status === 401) {
    clearSession();
    if (!location.pathname.endsWith("/login")) {
      const next = location.pathname.replace(/^\/cabinet\//, "");
      location.href = `/cabinet/login?next=${encodeURIComponent(next)}`;
    }
    return { ok: false, status: 401, error: "unauthorized" };
  }

  let body: unknown = null;
  const raw = await resp.text();
  if (raw) {
    try {
      body = JSON.parse(raw);
    } catch {
      body = raw;
    }
  }

  if (resp.ok) {
    return { ok: true, status: resp.status, data: body as T };
  }
  const message = extractApiError(body, resp.status);
  return { ok: false, status: resp.status, error: message };
}

export const api = {
  get: <T>(path: string) => apiRequest<T>(path, { method: "GET" }),
  post: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, {
      method: "POST",
      body: body == null ? undefined : JSON.stringify(body),
    }),
  patch: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, {
      method: "PATCH",
      body: body == null ? undefined : JSON.stringify(body),
    }),
  put: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, {
      method: "PUT",
      body: body == null ? undefined : JSON.stringify(body),
    }),
  delete: <T>(path: string) => apiRequest<T>(path, { method: "DELETE" }),
};
